/**
 * Admin Payments API Service
 * 
 * C1: Coins/Wallets Management
 * C2: Payment Monitoring
 */

import { logger } from "./logger";
import { apiClient } from "./apiClient";

// Note: apiClient already includes /api in baseURL, so we don't need to add it here
const API_BASE = "/admin/payments";

// ============================================================================
// Types
// ============================================================================

export interface CryptoAsset {
  id: string;
  symbol: string;
  network: string;
  token_contract: string | null;
  decimals: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateCryptoAssetRequest {
  symbol: string;
  network: string;
  token_contract?: string | null;
  decimals: number;
}

export interface UpdateCryptoAssetRequest {
  network?: string;
  token_contract?: string | null;
  decimals?: number;
  is_active?: boolean;
}

export interface CryptoWallet {
  id: string;
  asset_id: string;
  asset_symbol: string;
  address: string;
  label: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateCryptoWalletRequest {
  asset_id: string;
  address: string;
  label?: string | null;
}

export interface UpdateCryptoWalletRequest {
  address?: string;
  label?: string | null;
  is_active?: boolean;
}

export interface PaymentIntentListItem {
  id: string;
  user_id: string;
  user_email: string | null;
  plan_id: string;
  plan_code: string | null;
  amount_usd: number;
  method: string;
  status: string;
  tx_hash: string | null;
  verify_attempts: number;
  expires_at: string;
  created_at: string;
  verified_at: string | null;
}

export interface PaymentIntentListResponse {
  intents: PaymentIntentListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface VerifyNowResponse {
  success: boolean;
  status: string;
  reason: string | null;
  confirmations: number | null;
}

export interface PaymentListItem {
  id: string;
  user_id: string;
  user_email: string | null;
  intent_id: string;
  amount_usd: number;
  method: string;
  status: string;
  tx_hash: string | null;
  confirmed_at: string | null;
  created_at: string;
}

export interface PaymentListResponse {
  payments: PaymentListItem[];
  total: number;
  limit: number;
  offset: number;
}

// ============================================================================
// Crypto Assets API
// ============================================================================

export async function listCryptoAssets(): Promise<CryptoAsset[]> {
  try {
    const response = await apiClient.get<CryptoAsset[]>(`${API_BASE}/crypto/assets`);
    return response.data;
  } catch (error) {
    logger.error("Error listing crypto assets:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function createCryptoAsset(
  data: CreateCryptoAssetRequest,
): Promise<CryptoAsset> {
  try {
    const response = await apiClient.post<CryptoAsset>(`${API_BASE}/crypto/assets`, data);
    return response.data;
  } catch (error) {
    logger.error("Error creating crypto asset:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function updateCryptoAsset(
  assetId: string,
  data: UpdateCryptoAssetRequest,
): Promise<CryptoAsset> {
  try {
    const response = await apiClient.patch<CryptoAsset>(`${API_BASE}/crypto/assets/${assetId}`, data);
    return response.data;
  } catch (error) {
    logger.error("Error updating crypto asset:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function deactivateCryptoAsset(assetId: string): Promise<CryptoAsset> {
  try {
    const response = await apiClient.delete<CryptoAsset>(`${API_BASE}/crypto/assets/${assetId}`);
    return response.data;
  } catch (error) {
    logger.error("Error deactivating crypto asset:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function activateCryptoAsset(assetId: string): Promise<CryptoAsset> {
  try {
    const response = await apiClient.patch<CryptoAsset>(`${API_BASE}/crypto/assets/${assetId}`, {
      is_active: true,
    });
    return response.data;
  } catch (error) {
    logger.error("Error activating crypto asset:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

// ============================================================================
// Crypto Wallets API
// ============================================================================

export async function listCryptoWallets(
  assetId?: string,
): Promise<CryptoWallet[]> {
  try {
    const params = assetId ? { asset_id: assetId } : undefined;
    const response = await apiClient.get<CryptoWallet[]>(`${API_BASE}/crypto/wallets`, { params });
    return response.data;
  } catch (error) {
    logger.error("Error listing crypto wallets:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function createCryptoWallet(
  data: CreateCryptoWalletRequest,
): Promise<CryptoWallet> {
  try {
    const response = await apiClient.post<CryptoWallet>(`${API_BASE}/crypto/wallets`, data);
    return response.data;
  } catch (error) {
    logger.error("Error creating crypto wallet:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function updateCryptoWallet(
  walletId: string,
  data: UpdateCryptoWalletRequest,
): Promise<CryptoWallet> {
  try {
    const response = await apiClient.patch<CryptoWallet>(`${API_BASE}/crypto/wallets/${walletId}`, data);
    return response.data;
  } catch (error) {
    logger.error("Error updating crypto wallet:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function deactivateCryptoWallet(
  walletId: string,
): Promise<CryptoWallet> {
  try {
    const response = await apiClient.delete<CryptoWallet>(`${API_BASE}/crypto/wallets/${walletId}`);
    return response.data;
  } catch (error) {
    logger.error("Error deactivating crypto wallet:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

// ============================================================================
// Payment Intents API
// ============================================================================

export async function listPaymentIntents(params?: {
  status?: string;
  user_id?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}): Promise<PaymentIntentListResponse> {
  try {
    const response = await apiClient.get<PaymentIntentListResponse>(`${API_BASE}/intents`, { params });
    return response.data;
  } catch (error) {
    logger.error("Error listing payment intents:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

export async function verifyPaymentIntentNow(
  intentId: string,
): Promise<VerifyNowResponse> {
  try {
    const response = await apiClient.post<VerifyNowResponse>(`${API_BASE}/intents/${intentId}/verify-now`);
    return response.data;
  } catch (error) {
    logger.error("Error verifying payment intent:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

// ============================================================================
// Payments API
// ============================================================================

export async function listPayments(params?: {
  user_id?: string;
  status?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}): Promise<PaymentListResponse> {
  try {
    const response = await apiClient.get<PaymentListResponse>(`${API_BASE}/payments`, { params });
    return response.data;
  } catch (error) {
    logger.error("Error listing payments:", error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
}

